import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Slider } from "@mui/material";
import { useMeeting, usePubSub } from "@videosdk.live/react-sdk";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLogoFile, setLogoSize } from "../redux/logoSlice";
import { getLogos, createLogo, deleteLogo, updateMeetingLogo, createMeetingLogo } from "../Services/BannersApi";
import { FaEye, FaEyeSlash, FaTrash } from 'react-icons/fa';
import { createFileChangeHandler, triggerFileInput } from "../utils/fileUploadUtils";
import ImageUploadButton from "../Components/shared/ImageUploadButton";
import ImageGrid from "../Components/shared/ImageGrid";

const LogosPanel = () => {
    const { publish } = usePubSub("UPDATE_LOGO");
    const mMeeting = useMeeting();
    const dispatch = useDispatch();
    const logoData = useSelector((state) => state?.logo);
    const fileInputRef = useRef(null);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const meetingId = queryParams.get("meetingId");

    const [logos, setLogos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [selectedLogoId, setSelectedLogoId] = useState(null);
    const [hoveredLogoId, setHoveredLogoId] = useState(null);
    const [logoWidth, setLogoWidth] = useState(logoData?.logoSize || 10);

    useEffect(() => {
        fetchLogos();
    }, []);

    const fetchLogos = async () => {
        try {
            const response = await getLogos();
            setLogos(response?.Data);
            const savedLogoId = localStorage.getItem('selectedLogoId');
            if (savedLogoId) {
                setSelectedLogoId(savedLogoId);
            }
        } catch (error) {
            console.error('Error fetching logos:', error);
        }
    };

    const getLogoById = (id) => logos?.find(l => l.LogoID === id);

    const handleLogoClick = async (logoId) => {
        const logo = getLogoById(logoId);
        if (!logo) return;

        if (selectedLogoId === logoId) {
            setSelectedLogoId(null);
            dispatch(setLogoFile(null));
            publish({ LogoId: null, LogoUrl: null, visibility: false, width: null }, { persist: true });
            localStorage.removeItem('selectedLogoId');
            if (mMeeting?.isRecording) {
                await updateMeetingLogo({ LogoID: logo.LogoID, MeetingID: meetingId, EndTime: getCurrentTimestamp() });
            }
        } else {
            setSelectedLogoId(logoId);
            dispatch(setLogoFile(logo?.LogoUrl));
            publish({ LogoId: logo.LogoID, LogoUrl: logo.LogoUrl, visibility: true, width: `${logoWidth}%` }, { persist: true });
            localStorage.setItem('selectedLogoId', logo.LogoID);
            if (mMeeting?.isRecording) {
                await createMeetingLogo({ LogoID: logo.LogoID, MeetingID: meetingId, StartTime: getCurrentTimestamp() });
            }
        }
    };

    const handleWidthChange = (event, newValue) => {
        dispatch(setLogoSize(newValue));
        setLogoWidth(newValue);
        const selectedLogo = getLogoById(selectedLogoId);
        if (selectedLogo) {
            publish({ LogoId: selectedLogo.LogoID, LogoUrl: selectedLogo.LogoUrl, visibility: true, width: `${newValue}%` }, { persist: true });
        }
    };

    const handleFileChange = createFileChangeHandler(setUploading, 'LogoBase64', createLogo, fetchLogos);

    const handleDeleteLogo = async (logoId) => {
        try {
            if (selectedLogoId === logoId) {
                setSelectedLogoId(null);
                publish({ LogoId: null, LogoUrl: null, visibility: false, width: null }, { persist: true });
            }
            await deleteLogo(logoId);
            fetchLogos();
        } catch (error) {
            console.error("Error deleting logo:", error);
        }
    };

    function getCurrentTimestamp() {
        return new Date().toISOString();
    }

    const renderLogoOverlay = (logo, isSelected, isHovered) => (
        <div
            style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '1vh 2vw',
                borderRadius: '0.5vh',
                cursor: 'pointer',
                visibility: isSelected || isHovered ? 'visible' : 'hidden',
                display: 'flex',
                transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
            }}
        >
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    handleLogoClick(logo.LogoID);
                }}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
                {isSelected ? <FaEyeSlash /> : <FaEye />}
                <FaTrash
                    style={{ marginLeft: '1vh', cursor: 'pointer', color: 'red' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLogo(logo.LogoID);
                    }}
                />
            </div>
        </div>
    );

    return (
        <Box color="white">
            <div>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/png"
                    onChange={handleFileChange}
                />
                <ImageGrid
                    items={logos}
                    selectedId={selectedLogoId}
                    hoveredId={hoveredLogoId}
                    onHover={(id) => setHoveredLogoId(id)}
                    onLeave={() => setHoveredLogoId(null)}
                    onClick={(id) => handleLogoClick(id)}
                    getId={(logo) => logo.LogoID}
                    getUrl={(logo) => logo.LogoUrl}
                    getAlt={(logo) => `Logo ${logo.LogoID}`}
                    renderOverlay={renderLogoOverlay}
                />
                <ImageUploadButton
                    uploading={uploading}
                    onUpload={() => triggerFileInput(fileInputRef)}
                    label="Add a New Logo"
                />
                {selectedLogoId && (
                    <div style={{ marginTop: "2vh", width: "100%" }}>
                        <Typography variant="h6" style={{ fontSize: '1.6vh' }}>Adjust Logo Width</Typography>
                        <Slider
                            value={logoWidth}
                            min={1}
                            max={50}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value}`}
                            onChange={handleWidthChange}
                            style={{ width: "80%", marginLeft: "1vh" }}
                        />
                    </div>
                )}
            </div>
        </Box>
    );
};

export default LogosPanel;
